import { Record as record } from 'immutable';
import moment from 'moment';

import trainTime from '../lib/TrainTime';
import { TimeTypes, API_ISO_FORMAT } from '../constants/AppConstants';
import { construct, resolve } from '../helpers/LinkMapper';
import config from '../config';

export const DEFAULT_WINDOW_LENGTH = 3; // hours

export const PAST_LIMIT_SAFETY_HOURS =
    (config.get('journeyPlannerAllowDepartedServiceSearchTimeLimit') || 4) - 0.25;

const defaults = {
    datetime: trainTime(),
    arriveDepart: TimeTypes.DEPART,
    windowLength: DEFAULT_WINDOW_LENGTH
};

class Timeband extends record(defaults) {

    static fromJSON (json = {}) {
        return construct(Timeband, json, {
            datetime: resolve.with(trainTime),
            arriveDepart: resolve.with(String),
            windowLength: resolve.with(Number)
        });
    }

    toRange({ truncateStart, truncateEnd } = {}) {
        const isPastDate = moment(this.datetime).isBefore(moment());
        return isPastDate
            ? this.toRangePastDate()
            : this.toRangeFutureDate({ truncateStart, truncateEnd });
    }

    toRangeFutureDate({ truncateStart, truncateEnd }) {
        let rangeStart = this.getStart();
        let rangeEnd = this.getEnd();

        if (this.arriveDepart === TimeTypes.DEPART) {
            if (truncateStart && truncateStart.isAfter(rangeStart)) {
                rangeStart = truncateStart;
            }
            if (truncateEnd && truncateEnd.isBefore(rangeEnd)) {
                rangeEnd = truncateEnd;
            }
        }

        return this._formatRange(rangeStart, rangeEnd);
    }

    toRangePastDate() {
        let rangeStart = this.getStartInThePast();
        let rangeEnd = this.getEndInThePast();

        if (rangeEnd.isBefore(rangeStart)) {
            rangeEnd = rangeStart.clone().add(1, 'minute');
        }

        return this._formatRange(rangeStart, rangeEnd);
    }

    _formatRange(rangeStart, rangeEnd) {
        // Clamp rangeStart so it is never more than PAST_LIMIT_SAFETY_HOURS in the past
        const minRangeStart = moment().subtract(PAST_LIMIT_SAFETY_HOURS, 'hours');
        const clampedRangeStart = rangeStart.isBefore(minRangeStart) ? minRangeStart : rangeStart;

        // Ensure rangeEnd is not before the (possibly clamped) rangeStart
        let clampedRangeEnd = rangeEnd;
        if (clampedRangeEnd.isBefore(clampedRangeStart)) {
            clampedRangeEnd = clampedRangeStart.clone().add(1, 'minute');
        }

        return {
            rangeStart: clampedRangeStart.format(API_ISO_FORMAT),
            rangeEnd: clampedRangeEnd.format(API_ISO_FORMAT),
            arriveDepart: this.arriveDepart
        };
    }

    getWindowLength () {
        return { hours: this.windowLength };
    }

    getStart () {
        let start = this.datetime.clone();
        return (this.arriveDepart === TimeTypes.DEPART) ? start : start.subtract(this.getWindowLength());
    }

    getEnd () {
        let end = this.datetime.clone();
        return (this.arriveDepart === TimeTypes.ARRIVE) ? end : end.add(this.getWindowLength());
    }

    getStartInThePast () {
        const datetime = this.datetime.clone();
        const timeThresholdAgo = moment().subtract(config.get('journeyPlannerAllowDepartedServiceSearchTimeLimit'), 'hours');
        const rangeStart = this.arriveDepart === TimeTypes.ARRIVE
            ? datetime.subtract(this.getWindowLength())
            : datetime;

        const start = rangeStart.isBefore(timeThresholdAgo) ? timeThresholdAgo : rangeStart;
        return start.add(1, 'minute');
    }

    getEndInThePast () {
        const datetime = this.datetime.clone();
        return this.arriveDepart === TimeTypes.ARRIVE
            ? datetime.subtract(1, 'minute')
            : datetime.add(this.getWindowLength()).add(1, 'minute');
    }

    getEarlier (windowLength) {
        return this.subtract(windowLength ? { hours: windowLength } : this.getWindowLength());
    }

    getLater (windowLength) {
        return this.add(windowLength ? { hours: windowLength } : this.getWindowLength());
    }

    add () {
        return this.set('datetime', this.datetime.clone().add(...arguments));
    }

    subtract () {
        return this.set('datetime', this.datetime.clone().subtract(...arguments));
    }

}

export default Timeband;
